import React from "react";

import { fetchEmployees, STEP_COMPLETED } from "EmployerApplication/Actions";
import { ALL_BUSINESS_SUBCATEGORIES } from "EmployerApplication/Components/Constants";
import {
  CompanyName,
  BusinessCategory,
  BusinessSubcategory,
  PayrollRunPriorToCutoff,
  OrganizationType,
  DateEstablishedAt,
  PresidentName,
  Fein,
  ContactName,
  ContactTitle,
  ContactPhoneNumber,
  PartnerPrimaryPhoneNumber,
  PartnerPrimaryEmail,
  SameAsMailingAddress,
  MailAddress1,
  MailCity,
  MailState,
  MailZip,
  // NetSuiteExpenseAccount,
  // NetSuiteLiabilityAccount,
  PhysicalAddress1,
  PhysicalCity,
  PhysicalState,
  PhysicalZip,
  HasWorkersCompensation,
  WcCarrier,
  CoveringPartTime,
  DomesticPartnerCoverage,
  ProbationaryPeriod,
  LargeGroupMeasurementMethod,
  Ubi,
  NAICSCodeSearch,
} from "EmployerApplication/Components/FinalFormQuestions";
import createDecorator from "final-form-focus";
import isIntuit from "Intuit/isIntuit";
import { isRenewal } from "QuotePage/urls";
import { useFormState, Form } from "react-final-form";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { deserializeDate } from "Redux/serializers";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import { validateRequiredSignature, validateEmail } from "Utils/Validations";

import {
  useGetCurrentCompanyApplicationQuery,
  useUpdateCompanyApplicationMutation,
  useGetCurrentAccountQuery,
  useGetCompanyContributionsQuery,
  useGetUserExperienceFlagsQuery,
  useUpdateNetsuitedDeductionAccountsMutation,
  // useGetNetsuiteDeductionAccountsQuery,
} from "../api";
import { createAccountURL, addEmployeesURL, coverageOptionsURL } from "../urls";
import AgentHeader from "Common/Agent/AgentHeader";
import AppearingElement from "Common/AppearingElement";
import { SIFinalFormField, rtkQueryReponseToSubmissionError } from "Common/forms";
import { Row, Col } from "Common/Grid";
import NavigationControls from "Common/NavigationControls";
import { usePartnerContext } from "Common/PartnerContext";
import PartnerSyncedPropertiesSection from "Common/PartnerSyncedPropertiesSection";
import SignatureForm from "Common/Signature/SignatureForm";
import { useThemeContext } from "Theme/Context";
import { Surface, SurfaceHeader, SurfaceSection } from "Theme/Surface";

const focusOnErrors = createDecorator();

const LargeGroupMeasurementMethodRadio = () => {
  const formState = useFormState();
  const coveringPartTime = formState.values.company_application?.covering_part_time === "Yes";
  return <LargeGroupMeasurementMethod disabled={coveringPartTime} />;
};

const CompanyInformationPage = () => {
  const { intuit, cobalt } = useThemeContext();
  const dispatch = useDispatch();
  const history = useHistory();
  const { reviewOnlyFields, includeAccountCreationStep, partnerCasualName, partner, acaReporting } = usePartnerContext();
  const { data: companyApplication, isLoading } = useGetCurrentCompanyApplicationQuery();
  const companyPolicyId = companyApplication?.id;
  const { data: companyContributions } = useGetCompanyContributionsQuery(companyPolicyId, { skip: !companyPolicyId });

  const { data: account } = useGetCurrentAccountQuery();
  const [updateCompanyApplication] = useUpdateCompanyApplicationMutation();
  const [updateNetsuitedDeductionAccounts] = useUpdateNetsuitedDeductionAccountsMutation();
  const companyProfile = useSelector((state) => state.companyProfile);
  const { data: userExperienceFlags } = useGetUserExperienceFlagsQuery({
    companyIDToken: companyProfile.id_token,
  });

  const enteredSICCodeLifeModal = userExperienceFlags?.life_plan_modal_sic_code_entered && companyApplication?.sicCode;
  const isRenewalPolicy = companyApplication?.isRenewalPolicy;
  const showSignatureSection = !isRenewalPolicy;

  const getFormattedMailingAddress = (companyApplication) => {
    const { physicalAddress1, physicalCity, physicalState, physicalZip } = companyApplication;

    return `${physicalAddress1}, ${physicalCity}, ${physicalState}, ${physicalZip}`;
  };

  const reviewProperties = {
    companyName: companyApplication?.companyName,
    contactName: companyApplication?.contactName,
    contactTitle: companyApplication?.contactTitle,
    physicalAddress1: companyApplication?.physicalAddress1 ? getFormattedMailingAddress(companyApplication) : null,
    organizationType: companyApplication?.organizationType,
    fein: companyApplication?.fein,
    contactPhoneNumber: companyApplication?.contactPhoneNumber,
    dateEstablishedAt: companyApplication?.dateEstablishedAt ? companyApplication.dateEstablishedAt : null,
  };

  const SYNCED_FIELDS_LABEL_MAP = {
    companyName: "Company Name",
    contactName: "Contact Name",
    contactTitle: "Contact Title",
    physicalAddress1: "Address",
    organizationType: "Organization Type",
    fein: "Federal Tax ID Number",
    contactPhoneNumber: "Contact Phone Number",
    dateEstablishedAt: "Date Company Established",
  };

  const submitHandler = async (values) => {
    // DEPRECATED TRACKING
    AnalyticsTracking.trackEvent("step_form_next_clicked", {
      name: "employerApplicationForm",
      page: 1,
    });

    AnalyticsTracking.trackEvent("checked_out", {
      location: "ea-with-cart",
      deprecatedName: "checkout-button-clicked",
      is_renewal: isRenewal(),
    });

    // New Tracking
    AnalyticsTracking.trackEvent("button_clicked", {
      text: "Save and Continue",
      location: "Company Information Page",
    });

    try {
      if (partner === "netsuite") {
        await updateNetsuitedDeductionAccounts(values.preferences).unwrap();
      }
      const result = await updateCompanyApplication({
        id: companyPolicyId,
        ...values,
      }).unwrap();

      // for renewals, mark this step complete so navigationSaga moves to coverage options page
      if (isRenewalPolicy) {
        dispatch({
          type: STEP_COMPLETED,
          payload: "companyInformation",
        });
      }

      // LargeGroup ACA companies sometimes needs refresh employee data because
      // they change the employee's default "is_eligible" value.
      if (result.refreshEmployeeForms) dispatch(fetchEmployees());

      // new groups continue with existing next page logic
      if (!isRenewalPolicy) {
        // renewal groups DO NOT push a route here - navigationSaga will handle it
        if (includeAccountCreationStep && account.role == "dark") {
          history.push(createAccountURL);
        } else {
          history.push(addEmployeesURL);
        }
      }
    } catch (error) {
      return rtkQueryReponseToSubmissionError({ error });
    }
  };

  const handleClickBack = () => {
    // DEPRECATED TRACKING
    AnalyticsTracking.trackEvent("step_form_previous_clicked", {
      name: "employerApplicationForm",
      page: 1,
    });

    // New Tracking
    AnalyticsTracking.trackEvent("button_clicked", {
      text: "Back",
      location: "Company Information Page",
    });

    history.push(coverageOptionsURL);
  };

  const companyProfileInitialValues = (companyApplication) => {
    const companyProfileInitialValues = _.mapKeys(companyApplication, (_value, key) => _.snakeCase(key));

    // Delete company name so it doesn't show up as an email
    if (!validateEmail(companyProfileInitialValues.company_name)) {
      delete companyProfileInitialValues.company_name;
    }

    // Default to Yes for Same as Mailing Address if that's not populated yet.
    if (companyProfileInitialValues.same_as_mailing_address === null || companyProfileInitialValues.same_as_mailing_address.trim().length === 0) {
      companyProfileInitialValues.same_as_mailing_address ||= "Yes";
    }

    // Deserialize date from Redux store before passing to form
    companyProfileInitialValues.payroll_start_cutoff_date = deserializeDate(companyProfileInitialValues.payroll_start_cutoff_date);

    return companyProfileInitialValues;
  };

  const initialProbationaryPeriod = (companyApplication) => {
    if (companyApplication.probationaryPeriod === null) {
      return "1st of month after hire date";
    }
    return companyApplication.probationaryPeriod;
  };

  let initialValues;

  if (!isLoading) {
    const hasPartTimeContribution = companyContributions && companyContributions.some((contribution) => contribution.role === "part_time");

    const companyPolicyInitialValues = {
      covering_part_time: hasPartTimeContribution || companyApplication.coveringPartTime,
      domestic_partner_coverage: companyApplication.domesticPartnerCoverage,
      probationary_period: initialProbationaryPeriod(companyApplication),
    };

    initialValues = {
      company_application: {
        ...companyPolicyInitialValues,
        ...companyProfileInitialValues(companyApplication),
      },

      signature: {
        authorization_signature: companyApplication.authorizationSignature,
      },

      preferences: companyApplication.partner_company_preferences,
    };
  }

  const CompanyStartDates = () => {
    const formState = useFormState();

    const cutoffDate = formState.values.company_application.payroll_start_cutoff_date;

    return (
      <>
        {!reviewOnlyFields.dateEstablishedAt && (
          <Row>
            <DateEstablishedAt lg={10} />
          </Row>
        )}

        <Row>
          <PayrollRunPriorToCutoff lg={10} cutoffDate={cutoffDate} />
        </Row>
      </>
    );
  };

  const BusinessCategories = ({ resetBusinessSubcategory }) => {
    const formState = useFormState();
    const category = formState.values.company_application?.business_category;
    const subCategoryOptions = category ? ALL_BUSINESS_SUBCATEGORIES[category] : {};

    return (
      <>
        <Row>
          <BusinessCategory lg={10} onChange={resetBusinessSubcategory} />
        </Row>
        <AppearingElement show={category != undefined}>
          <Row>
            <BusinessSubcategory lg={10} subCategoryOptions={subCategoryOptions} />
          </Row>
        </AppearingElement>
      </>
    );
  };

  const WorkersCompensation = () => {
    const formState = useFormState();
    const hasWorkersCompensation = formState.values.company_application?.has_workers_compensation === "Yes";

    return (
      <>
        <Row>
          <HasWorkersCompensation lg={10} />
        </Row>
        <Row>
          <WcCarrier lg={10} disabled={!hasWorkersCompensation} />
        </Row>
      </>
    );
  };

  const Addresses = () => {
    const formState = useFormState();
    const sameAsMailingAddress = formState.values.company_application?.same_as_mailing_address === "Yes";

    return (
      <>
        {!reviewOnlyFields.physicalAddress1 && (
          <>
            <Row>
              <PhysicalAddress1 lg={10} />
            </Row>
            <Row>
              <PhysicalCity lg={10} />
            </Row>
            <Row>
              <PhysicalState lg={10} componentProps={{ disabled: true }} />
            </Row>
            <Row>
              <PhysicalZip lg={10} componentProps={{ disabled: true }} />
            </Row>
          </>
        )}

        <Row>
          <SameAsMailingAddress lg={10} />
        </Row>
        <AppearingElement show={!sameAsMailingAddress}>
          <Row>
            <MailAddress1 lg={10} />
          </Row>
          <Row>
            <MailCity lg={10} />
          </Row>
          <Row>
            <MailState lg={10} />
          </Row>
          <Row>
            <MailZip lg={10} />
          </Row>
        </AppearingElement>
      </>
    );
  };

  // keepDirtyOnReinitialize is needed because the mutation will cause a state change, which causes a re-render.  When re-rendering, it'll
  // use stale companyApplication to generate initial values.  This results in the form visually "resetting" during the update, even though
  // the value is changed correctly.

  const isKaiserOrPremera = () => {
    return companyApplication?.planBrand === "Kaiser Permanente" || companyApplication?.planBrand === "Premera Blue Cross";
  };
  const showAcaQuestion = companyProfile.is_large_group && acaReporting;

  if (!companyPolicyId) return;

  return (
    <Surface>
      <SurfaceHeader>Enter Information</SurfaceHeader>
      <SurfaceSection>
        <AgentHeader>Great! Next, let's gather some basic information about your company.</AgentHeader>
      </SurfaceSection>
      <Form onSubmit={submitHandler} decorators={[focusOnErrors]} initialValues={initialValues} keepDirtyOnReinitialize>
        {({ handleSubmit, submitting, form }) => (
          <form onSubmit={handleSubmit}>
            {Object.keys(reviewOnlyFields).length > 0 && (
              <>
                {/* TODO - remove SurfaceHeader in favor of section title prop when Cobalt launches */}
                {(!cobalt || (intuit && isIntuit())) && <SurfaceHeader>{`Synced Information from ${partnerCasualName}`}</SurfaceHeader>}

                <PartnerSyncedPropertiesSection
                  isLoading={false}
                  sectionTitle={`Synced Information from ${partnerCasualName}`}
                  syncedProperties={Object.keys(reviewOnlyFields).map((key) => [SYNCED_FIELDS_LABEL_MAP[key], reviewProperties[key]])}
                />
              </>
            )}

            {(!cobalt || (intuit && isIntuit())) && <SurfaceHeader>Company Information</SurfaceHeader>}

            <SurfaceSection title="Company Information">
              {!reviewOnlyFields.companyName && (
                <Row>
                  <CompanyName lg={10} />
                </Row>
              )}

              {!enteredSICCodeLifeModal && !isKaiserOrPremera() && (
                <BusinessCategories resetBusinessSubcategory={() => form.change("company_application[sic_code]", undefined)} />
              )}

              {isKaiserOrPremera() && (
                <Row>
                  <NAICSCodeSearch lg={10} shouldPrefillNAICSCode />
                </Row>
              )}

              {!reviewOnlyFields.organizationType && (
                <Row>
                  <OrganizationType lg={10} />
                </Row>
              )}

              {!reviewOnlyFields.fein && (
                <Row>
                  <Fein lg={10} />
                </Row>
              )}

              {companyApplication?.physicalState === "WA" && (
                <Row>
                  <Ubi lg={10} />
                </Row>
              )}

              <Row>
                <PresidentName lg={10} />
              </Row>
              <CompanyStartDates />
            </SurfaceSection>
            {(!cobalt || (intuit && isIntuit())) && <SurfaceHeader>Contact Information</SurfaceHeader>}

            <SurfaceSection title="Contact Information">
              {!reviewOnlyFields.contactName && (
                <Row>
                  <ContactName lg={10} />
                </Row>
              )}

              {!reviewOnlyFields.contactTitle && (
                <Row>
                  <ContactTitle lg={10} />
                </Row>
              )}

              <Row>
                {reviewOnlyFields.contactPhoneNumber ? (
                  <PartnerPrimaryPhoneNumber lg={10} placeholder={companyApplication?.contactPhoneNumber} />
                ) : (
                  <ContactPhoneNumber lg={10} />
                )}
              </Row>
              <Row>
                <PartnerPrimaryEmail lg={10} />
              </Row>
              <Addresses />
            </SurfaceSection>
            {(!cobalt || (intuit && isIntuit())) && <SurfaceHeader>Worker's Compensation Details</SurfaceHeader>}

            <SurfaceSection title="Worker's Compensation Details">
              <WorkersCompensation />
            </SurfaceSection>
            {(!cobalt || (intuit && isIntuit())) && <SurfaceHeader>Coverage Preferences</SurfaceHeader>}

            <SurfaceSection title="Coverage Preferences">
              {!isRenewalPolicy && (
                <Row mb={3}>
                  <ProbationaryPeriod lg={10} />
                </Row>
              )}
              <Row>
                <DomesticPartnerCoverage lg={10} />
              </Row>
              <Row>
                <CoveringPartTime lg={10} mt={3} />
              </Row>
              {showAcaQuestion && (
                <Row>
                  <Col>
                    <LargeGroupMeasurementMethodRadio />
                  </Col>
                </Row>
              )}
            </SurfaceSection>

            {showSignatureSection && (!cobalt || (intuit && isIntuit())) && <SurfaceHeader>Signature</SurfaceHeader>}

            {/* {partner === "netsuite" && <NetSuiteSelectAccount />} */}
            {showSignatureSection && (
              <SurfaceSection title="Signature">
                <Row>
                  <SIFinalFormField
                    lg={10}
                    name="signature[authorization_signature]"
                    label="Sign below to authorize SimplyInsured to send the information you have provided to the insurance company to begin coverage."
                    component={SignatureForm}
                    componentProps={{
                      showSubmitButton: false,
                      clearButtonText: "Clear Signature",
                      responsive: true,
                    }}
                    validate={validateRequiredSignature}
                    errorRight
                  />
                </Row>
              </SurfaceSection>
            )}
            <NavigationControls onClickBack={handleClickBack} submitting={submitting} />
          </form>
        )}
      </Form>
    </Surface>
  );
};

export default CompanyInformationPage;
